import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";

type ButtonVariant = "primary" | "secondary" | "danger" | "dangerOutline";

type ButtonSize = "sm" | "md" | "icon";

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-8 px-3",
  md: "h-10 px-4",
  icon: "h-8 w-8",
};

const variantClasses: Record<ButtonVariant, string> = {
  primary: "bg-accent text-surface hover:bg-accent/80",
  secondary: "bg-muted text-foreground hover:bg-muted/80",
  danger: "bg-danger text-background hover:bg-danger/80",
  dangerOutline:
    "border border-danger/45 bg-transparent text-danger hover:bg-danger/10",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

function classesFor(
  variant: ButtonVariant,
  size: ButtonSize,
  className: string,
) {
  return `inline-flex items-center justify-center rounded-lg text-sm disabled:opacity-50 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`;
}

export function Button({
  variant = "secondary",
  size = "md",
  className = "",
  type = "button",
  children,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={classesFor(variant, size, className)}
      {...props}
    >
      {children}
    </button>
  );
}

interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}

/** A link that navigates away from the console but reads as a button. */
export function ButtonLink({
  variant = "secondary",
  size = "md",
  className = "",
  children,
  ...props
}: ButtonLinkProps) {
  return (
    <a className={classesFor(variant, size, className)} {...props}>
      {children}
    </a>
  );
}
